import { KeyboardEvent, MouseEvent, Ref, forwardRef } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import styled from 'styled-components';
import { rem } from 'polished';
import {
  StyledConflictListItem,
  StyledConflictListItemText,
  StyledConflictListItemTextWrapper,
  StyledConflictListItemWrapper,
  StyledConflictQuantityLabel,
} from './ConflictListItem.styles';
import QuantityStepper from '@molecules/ProductQuantityControl/QuantityStepper';
import useQuantityStepper from '@molecules/ProductQuantityControl/useQuantityStepper';
import getPickUnit from '@helpers/getPickUnit';
import { AxfoodReplacementProductViewModel, UnitData } from '@occ/api-client';
import WillysImagePlaceholder from '@icons/non-standard/WILLYS-IMAGE-PLACEHOLDER-72px.svg';
import useResponsive from '@hooks/useResponsive';

export type ConflictListItemType = 'conflicting' | 'replaceable' | 'non-replaceable';

const ImageWrapper = styled.div`
  min-width: ${rem(68)};
  min-height: ${rem(68)};
  width: ${rem(68)};
  height: ${rem(68)};
`;

interface Props {
  product: AxfoodReplacementProductViewModel & { pickUnit?: UnitData };
  editable?: boolean;
  variant: ConflictListItemType;
  selected?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  onQuantityChange?: (qty: number) => void;
  pickQuantity: number;
  availableQuantity: number;
  partialOutOfStock?: boolean;
  addToCartDisabled?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
}

const ConflictListItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      product,
      editable = false,
      variant,
      selected = false,
      onClick,
      onQuantityChange,
      pickQuantity,
      availableQuantity,
      addToCartDisabled = false,
      onKeyDown,
    }: Props,
    ref: Ref<HTMLDivElement>
  ) => {
    const t = useTranslations('conflictListItem');
    const { isMobile, fromTabletPortrait } = useResponsive();
    const { productBasketType = {}, name, productLine2, potentialPromotions, price, depositPrice, image } = product;
    const recycleText = depositPrice ? t('depositPrice') : '';
    const unitTypes = {
      VIKT: 'kg',
      ST: 'st',
    };

    const unit = product?.pickUnit?.code || getPickUnit(product?.productBasketType?.code);

    const {
      inputRef,
      currentQuantity: quantity,
      isActive,
      onClickHandler: onAmountChangeClickHandler,
      onBlurHandler,
      onChangeHandler,
      onFocusHandler,
      onKeyUpHandler,
      onKeyDownHandler,
    } = useQuantityStepper({
      initialQuantity: product.replacementQty || 0,
      unit: (unit as 'pieces' | 'kilogram') ?? 'pieces',
      incrementValue: product.incrementValue,
      onQuantityChange: (newQuantity) => {
        if (onQuantityChange) onQuantityChange(newQuantity);
      },
      blurOnlyWhenActive: true,
      stopPropagationWhenActive: true,
    });

    const outOfStockText =
      pickQuantity > 1
        ? `${t(variant === 'non-replaceable' ? 'partiallyRemoveItems' : 'partiallyOutOfStock', {
            amountToReplace: pickQuantity - availableQuantity,
            totalAmount: pickQuantity,
            unit: unitTypes[productBasketType.code as keyof typeof unitTypes],
          })} `
        : '';

    return (
      <StyledConflictListItem
        ref={ref}
        $variant={variant}
        $isSelected={selected}
        onClick={onClick}
        onKeyDown={onKeyDown}
        $disabledAddToCart={!onClick}
        // If item has onclick attached, make it tabbable and set role
        tabIndex={onClick ? 0 : undefined}
        role={onClick ? 'button' : undefined}
      >
        <StyledConflictListItemWrapper>
          {addToCartDisabled && (
            <>
              <ImageWrapper>
                {image?.url ? (
                  <Image unoptimized src={image?.url} alt={name} width={68} height={68} />
                ) : (
                  <WillysImagePlaceholder />
                )}
              </ImageWrapper>
              <StyledConflictListItemTextWrapper>
                <StyledConflictListItemText align="left" type="subhead">
                  {name}
                </StyledConflictListItemText>
              </StyledConflictListItemTextWrapper>
            </>
          )}

          {!addToCartDisabled && (
            <>
              <ImageWrapper>
                {image?.url ? (
                  <Image unoptimized src={image?.url} alt={name} width={68} height={68} />
                ) : (
                  <WillysImagePlaceholder />
                )}
              </ImageWrapper>
              <StyledConflictListItemTextWrapper>
                {isMobile && (
                  <StyledConflictListItemText align="left" type="body" size="small" color="red">
                    {outOfStockText}
                  </StyledConflictListItemText>
                )}
                <StyledConflictListItemText type="subhead" align="left">
                  {name}
                </StyledConflictListItemText>
                <StyledConflictListItemText align="left" type="detail" size="small" color="darkerGray">
                  {productLine2}
                </StyledConflictListItemText>
                {potentialPromotions?.length > 0 ? (
                  <StyledConflictListItemText align="left" type="subhead" color="red">
                    {`${potentialPromotions[0].price?.formattedValue} ${recycleText}`}
                  </StyledConflictListItemText>
                ) : (
                  <StyledConflictListItemText
                    align="left"
                    type="subhead"
                  >{`${price} ${recycleText}`}</StyledConflictListItemText>
                )}
              </StyledConflictListItemTextWrapper>
              {fromTabletPortrait && (
                <StyledConflictQuantityLabel align="left" type="body" size="small" color="red">
                  {outOfStockText}
                </StyledConflictQuantityLabel>
              )}
              {editable && onQuantityChange && (
                <QuantityStepper
                  variant="cart"
                  inputRef={inputRef}
                  onBlurHandler={onBlurHandler}
                  onChangeHandler={onChangeHandler}
                  onFocusHandler={onFocusHandler}
                  onKeyUpHandler={onKeyUpHandler}
                  onKeyDownHandler={onKeyDownHandler}
                  onClickHandler={onAmountChangeClickHandler}
                  isActive={isActive}
                  currentQuantity={quantity}
                  unit={unit}
                  incrementValue={product.incrementValue}
                  outOfStock={product.outOfStock}
                />
              )}
            </>
          )}
        </StyledConflictListItemWrapper>
      </StyledConflictListItem>
    );
  }
);

ConflictListItem.displayName = 'ConflictListItem';

export default ConflictListItem;
